import { resolveSemanticColor } from '@/theme/semanticTokens';
import { s } from '@/theme/tokens';
import { Box, Panel, Stack, Text } from '@/ui';
import { settingsPanelSurfaceSx } from '@/ui/composites/settingsPageTypographySx';
import { SummaryTooltipLines } from '@/ui/composites/table/cells/summaryCellUtils';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatDuration } from '@/ui/formatters/formatDuration';
import { ErrorOutline, HelpOutline, SuitesIcon, TestsIcon, Tooltip } from '@/ui/primitives';
import {
  BlockOutlined,
  CancelOutlinedIcon,
  CheckCircleOutlineIcon,
  EventOutlinedIcon,
  TimerOutlinedIcon,
} from '@/ui/primitives/icons';
import { mappingTooltipSlotProps } from './projectCsvMappingSectionShared';
import type { ValidationPreviewStatus } from './validationDataChecks';
import type { ReactElement } from 'react';

type ValidationPreviewOverallStatusPanelProps = {
  overallStatus: ValidationPreviewStatus;
  suiteCounts: Record<ValidationPreviewStatus, number>;
  testCounts: Record<ValidationPreviewStatus, number>;
  suiteNames: string[];
  suiteNamesByStatus: Record<ValidationPreviewStatus, string[]>;
  testNames: string[];
  testNamesByStatus: Record<ValidationPreviewStatus, string[]>;
  totalSuites: number;
  totalTests: number;
  totalDurationMs: number | null;
  latestTimestamp: Date | null;
  container?: 'panel' | 'embedded';
};

const summaryStatusOrder: ValidationPreviewStatus[] = [
  'passed',
  'failed',
  'errored',
  'skipped',
  'untested',
];

type ValidationPreviewCountListEntry = {
  key: string;
  colorToken: 'success' | 'error' | 'warning' | 'brown' | 'textSecondary';
  icon: ReactElement;
  label: string;
  tooltipLines: string[];
};

const getStatusPresentation = (
  status: ValidationPreviewStatus,
): {
  label: string;
  colorToken: 'success' | 'error' | 'warning' | 'brown' | 'textSecondary';
  icon: ReactElement;
} => {
  switch (status) {
    case 'passed':
      return {
        label: 'Passed',
        colorToken: 'success',
        icon: <CheckCircleOutlineIcon fontSize="small" sx={{ fontSize: 16 }} />,
      };
    case 'failed':
      return {
        label: 'Failed',
        colorToken: 'error',
        icon: <CancelOutlinedIcon fontSize="small" sx={{ fontSize: 16 }} />,
      };
    case 'errored':
      return {
        label: 'Errored',
        colorToken: 'brown',
        icon: <ErrorOutline fontSize="small" sx={{ fontSize: 16 }} />,
      };
    case 'skipped':
      return {
        label: 'Skipped',
        colorToken: 'warning',
        icon: <BlockOutlined fontSize="small" sx={{ fontSize: 16 }} />,
      };
    case 'untested':
      return {
        label: 'Untested',
        colorToken: 'textSecondary',
        icon: <HelpOutline fontSize="small" sx={{ fontSize: 16 }} />,
      };
  }
};

const ValidationPreviewCountList = ({
  counts,
  total,
  totalLabel,
  totalIcon,
  totalNames,
  namesByStatus,
}: {
  counts: Record<ValidationPreviewStatus, number>;
  total: number;
  totalLabel: string;
  totalIcon: ReactElement;
  totalNames: string[];
  namesByStatus: Record<ValidationPreviewStatus, string[]>;
}): ReactElement => {
  const entries: ValidationPreviewCountListEntry[] = [
    {
      key: `${totalLabel}-total`,
      colorToken: 'textSecondary',
      icon: totalIcon,
      label: `${total.toLocaleString('en-GB')} ${totalLabel.toLowerCase()}`,
      tooltipLines: totalNames,
    },
    ...summaryStatusOrder
      .filter((status) => counts[status] > 0)
      .map((status) => {
        const presentation = getStatusPresentation(status);
        return {
          key: `${totalLabel}-${status}`,
          colorToken: presentation.colorToken,
          icon: presentation.icon,
          label: `${counts[status].toLocaleString('en-GB')} ${presentation.label.toLowerCase()}`,
          tooltipLines: namesByStatus[status],
        };
      }),
  ];
  const columnCount = entries.length > 6 ? 3 : entries.length > 3 ? 2 : 1;
  const rowsPerColumn = Math.ceil(entries.length / columnCount);
  const columns = Array.from({ length: columnCount }, (_, columnIndex) =>
    entries.slice(columnIndex * rowsPerColumn, (columnIndex + 1) * rowsPerColumn),
  );

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
        columnGap: s('xs'),
        rowGap: s('none'),
        justifyItems: 'start',
      }}
    >
      {columns.map((columnEntries, columnIndex) => (
        <Stack key={`${totalLabel}-column-${columnIndex}`} gap="none">
          {columnEntries.map((entry) => (
            <Box
              key={entry.key}
              sx={{ display: 'flex', alignItems: 'center', gap: s('sm'), py: '2px' }}
            >
              <Box
                sx={(theme) => ({
                  display: 'inline-flex',
                  color: resolveSemanticColor(theme, entry.colorToken),
                })}
              >
                {entry.icon}
              </Box>
              <Tooltip
                title={<SummaryTooltipLines lines={entry.tooltipLines} />}
                placement="top"
                arrow
                disableHoverListener={entry.tooltipLines.length === 0}
                slotProps={mappingTooltipSlotProps}
              >
                <Box component="span" sx={{ display: 'inline-flex' }}>
                  <Text role="body" color="textSecondary" sx={{ lineHeight: 1.2 }}>
                    {entry.label}
                  </Text>
                </Box>
              </Tooltip>
            </Box>
          ))}
        </Stack>
      ))}
    </Box>
  );
};

export const ValidationPreviewOverallStatusPanel = ({
  overallStatus,
  suiteCounts,
  testCounts,
  suiteNames,
  suiteNamesByStatus,
  testNames,
  testNamesByStatus,
  totalSuites,
  totalTests,
  totalDurationMs,
  latestTimestamp,
  container = 'panel',
}: ValidationPreviewOverallStatusPanelProps): ReactElement => {
  const overallPresentation = getStatusPresentation(overallStatus);
  const durationLabel = formatDuration(totalDurationMs) || '-';
  const timestampLabel = latestTimestamp ? formatDate(latestTimestamp, { includeTime: true }) : '-';

  const content = (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          md: 'repeat(3, minmax(0, 1fr))',
        },
        gap: s('lg'),
      }}
    >
      <Stack gap="xs">
        <Text role="body" color="textPrimary">
          Overall Status
        </Text>
        <Stack gap="none">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: s('sm'), py: '2px' }}>
            <Box
              sx={(theme) => ({
                display: 'inline-flex',
                color: resolveSemanticColor(theme, overallPresentation.colorToken),
              })}
            >
              {overallPresentation.icon}
            </Box>
            <Text role="body" color="textSecondary" sx={{ lineHeight: 1.2 }}>
              {overallPresentation.label}
            </Text>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: s('sm'), py: '2px' }}>
            <Tooltip title="Duration" placement="top" arrow slotProps={mappingTooltipSlotProps}>
              <Text
                component="span"
                role="body"
                sx={(theme) => ({
                  display: 'inline-flex',
                  color: resolveSemanticColor(theme, 'textSecondary'),
                })}
              >
                <TimerOutlinedIcon sx={{ fontSize: 16 }} />
              </Text>
            </Tooltip>
            <Text role="body" color="textSecondary" sx={{ lineHeight: 1.2 }}>
              {durationLabel}
            </Text>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: s('sm'), py: '2px' }}>
            <Tooltip title="Timestamp" placement="top" arrow slotProps={mappingTooltipSlotProps}>
              <Text
                component="span"
                role="body"
                sx={(theme) => ({
                  display: 'inline-flex',
                  color: resolveSemanticColor(theme, 'textSecondary'),
                })}
              >
                <EventOutlinedIcon sx={{ fontSize: 16 }} />
              </Text>
            </Tooltip>
            <Text role="body" color="textSecondary" sx={{ lineHeight: 1.2 }}>
              {timestampLabel}
            </Text>
          </Box>
        </Stack>
      </Stack>
      <Stack gap="xs">
        <Text role="body" color="textPrimary">
          Suites
        </Text>
        <ValidationPreviewCountList
          counts={suiteCounts}
          total={totalSuites}
          totalLabel="Suites"
          totalIcon={<SuitesIcon sx={{ fontSize: 16 }} />}
          totalNames={suiteNames}
          namesByStatus={suiteNamesByStatus}
        />
      </Stack>
      <Stack gap="xs">
        <Text role="body" color="textPrimary">
          Tests
        </Text>
        <ValidationPreviewCountList
          counts={testCounts}
          total={totalTests}
          totalLabel="Tests"
          totalIcon={<TestsIcon sx={{ fontSize: 16 }} />}
          totalNames={testNames}
          namesByStatus={testNamesByStatus}
        />
      </Stack>
    </Box>
  );

  if (container === 'embedded') {
    return content;
  }

  return (
    <Panel footerGap="xs" surfaceVariant="settings" sx={settingsPanelSurfaceSx}>
      {content}
    </Panel>
  );
};
